import React, { useMemo, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { MRT_ColumnDef, useMaterialReactTable, MRT_Row, MRT_TableOptions, MRT_SortingState } from 'material-react-table';
import { LegalEntityNameAliasDto, UpsertLegalEntityAliasRequest } from 'src/generated/services/TFinancialApi';
import { SettingsTableMain } from 'src/presentations/components/molecules/settings/table/TableMain';
import { Box, IconButton, Tooltip, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeleteLegalEntityAlias, useUpsertLegalEntityAlias } from 'src/use-cases/settings/useLegalEntities';
import { openToast } from 'src/use-cases/toast/useToast';
import { ModalConfirm } from 'src/presentations/components/molecules/modal';

interface LegalEntityAliasTableProps {
  aliases: LegalEntityNameAliasDto[];
  legalEntityId: number;
}

export const LegalEntityAliasTable = forwardRef<any, LegalEntityAliasTableProps>(({ aliases, legalEntityId }, ref) => {
  const [rowToDelete, setRowToDelete] = useState<LegalEntityNameAliasDto | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [isMutating, setIsMutating] = useState(false);

  // Default sort by ID in ascending order
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: 'id',
      desc: false,
    },
  ]);

  const { mutateAsync: deleteAlias, isLoading: isDeleting } = useDeleteLegalEntityAlias();
  const { mutateAsync: upsertAlias } = useUpsertLegalEntityAlias();

  // Helper function to clear validation errors
  const clearValidationError = useCallback((fieldName: string) => {
    setValidationErrors((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));
  }, []);

  // Validate alias fields
  const onValidateAlias = useCallback((alias: Record<string, any>) => {
    const validationResult = {
      name: !alias.name || alias.name.trim() === '' ? 'Alias name is required' : '',
      type: !alias.type || alias.type.trim() === '' ? 'Alias type is required' : '',
    };
    return validationResult;
  }, []);

  // CREATE action
  const handleCreateAlias: MRT_TableOptions<LegalEntityNameAliasDto>['onCreatingRowSave'] = async ({ values, exitCreatingMode }) => {
    const newValidationErrors = onValidateAlias(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    try {
      setIsMutating(true);

      const reqBody: UpsertLegalEntityAliasRequest = {
        legalEntityId,
        name: values.name,
        type: values.type,
      };

      await upsertAlias(reqBody);
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Alias created successfully!',
      });
      exitCreatingMode();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    } finally {
      setIsMutating(false);
    }
  };

  // UPDATE action
  const handleUpdateAlias: MRT_TableOptions<LegalEntityNameAliasDto>['onEditingRowSave'] = async ({ row, values, exitEditingMode }) => {
    const newValidationErrors = onValidateAlias(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    try {
      setIsMutating(true);

      const reqBody: UpsertLegalEntityAliasRequest = {
        id: row.original.id,
        legalEntityId,
        name: values.name,
        type: values.type,
      };

      await upsertAlias(reqBody);
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Alias updated successfully!',
      });
      exitEditingMode();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    } finally {
      setIsMutating(false);
    }
  };
  const handleDeleteAlias = async (alias: LegalEntityNameAliasDto) => {
    if (!alias.id) {
      openToast({
        toastType: 'error',
        title: 'Error',
        description: 'Cannot delete alias: missing ID',
      });
      return;
    }

    try {
      await deleteAlias(alias.id);
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Alias deleted successfully!',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    }
  };

  // Render row actions for aliases
  const renderRowActions = useCallback(
    ({ row, table }: { row: MRT_Row<LegalEntityNameAliasDto>; table: any }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => setRowToDelete(row.original)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    []
  );
  // Define columns for the Alias table
  const aliasColumns = useMemo<MRT_ColumnDef<LegalEntityNameAliasDto>[]>(
    () => [
      {
        enableEditing: false,
        accessorKey: 'id',
        header: 'Id',
        size: 100,
      },
      {
        accessorKey: 'name',
        header: 'Alias',
        size: 300,
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () => clearValidationError('name'),
        },
      },
      {
        accessorKey: 'type',
        header: 'Alias Type',
        size: 150,
        muiEditTextFieldProps: {
          select: true,
          required: true,
          error: !!validationErrors?.type,
          helperText: validationErrors?.type,
          onFocus: () => clearValidationError('type'),
          children: [
            <MenuItem key='NAME' value='NAME'>
              Name
            </MenuItem>,
            <MenuItem key='ACCOUNT_NUMBER' value='ACCOUNT_NUMBER'>
              Account Number
            </MenuItem>,
            <MenuItem key='CITY' value='CITY'>
              City
            </MenuItem>,
          ],
        },
      },
    ],
    [validationErrors, clearValidationError]
  );

  // Configure the alias table
  const aliasTable = useMaterialReactTable({
    columns: aliasColumns,
    data: aliases || [],
    enableSorting: true,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableRowActions: true,
    enableEditing: true,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    positionActionsColumn: 'first',
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateAlias,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleUpdateAlias,
    state: {
      sorting,
      isLoading: isMutating,
    },
    onSortingChange: setSorting,
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
      },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 120,
        muiTableHeadCellProps: {
          align: 'center',
        },
      },
    },
    renderEmptyRowsFallback: () => <p className='text-center py-4'>No aliases found</p>,
    renderRowActions,
  });

  // Expose table methods to parent component
  useImperativeHandle(ref, () => ({
    setCreatingRow: (creating: boolean) => {
      if (creating) {
        aliasTable.setCreatingRow(true);
      } else {
        aliasTable.setCreatingRow(null);
      }
    },
  }));

  return (
    <div className='overflow-x-auto'>
      <SettingsTableMain table={aliasTable} />

      <ModalConfirm
        open={!!rowToDelete}
        title='Warning'
        isLoading={isDeleting}
        onAccept={async () => {
          if (rowToDelete) {
            await handleDeleteAlias(rowToDelete);
            setRowToDelete(null);
          }
        }}
        onCancel={() => setRowToDelete(null)}
        buttonAcceptLabel='Delete'
        buttonCancelLabel='Cancel'
      >
        <p>Are you sure you want to delete this alias?</p>
      </ModalConfirm>
    </div>
  );
});

// Set display name for devtools
LegalEntityAliasTable.displayName = 'LegalEntityAliasTable';
